import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import NextLink from 'next/link';
import { ProductAvailabilityTags, ProductColorSwatches, getProductColorOptions } from '@loomstateorg/shared-frontend';
import type { ProductColorOption } from '@loomstateorg/shared-frontend';
import { IconText } from '@loomstateorg/shared-frontend/components/commercetools-ui/organisms/greenland-score';
// TODO: implement a better design for quickview
import Prices from '@loomstateorg/shared-frontend/components/commercetools-ui/organisms/product/product-tile/prices';
import SustainabilityBadge from '@loomstateorg/shared-frontend/components/commercetools-ui/organisms/product/product-tile/sustainability-badge';
import { SustainabilityItem } from 'components/commercetools-ui/types';
import useMediaQuery from '@loomstateorg/shared-frontend/hooks/useMediaQuery';
import useProductTileFeatures from 'helpers/hooks/useProductTileFeatures';
import useVariantWithDiscount from '@loomstateorg/shared-frontend/hooks/useVariantWithDiscount';
import { desktop } from '@loomstateorg/shared-frontend/utils/screensizes';
import { Product, Variant } from 'types/product';
import useImpacts from 'frontastic/hooks/useImpacts';
import Image from 'frontastic/lib/image';
import useTrack from '@loomstateorg/shared-frontend/components/commercetools-ui/organisms/product/product-tile/useTrack';

export interface ProductTileProps {
  product: Product;
  onClick?: () => void;
  isSearchResult?: boolean;
  disableVariants?: boolean;
  style?: object;
  sustainability?: {
    certificationsItem?: SustainabilityItem;
    environmentalItem?: SustainabilityItem;
  };
  dataTestId?: string;
  index?: number;
  showSecondImageOnHover?: boolean;
  imageApi?: 'fill' | 'layout';
  imageWrapperClassName?: string;
  imageAspectClassName?: string;
  imageObjectFit?: 'cover' | 'contain';
  availabilityTagsClassName?: string;
  availabilityTagClassName?: string;
  availabilityTagClassNameByKey?: Record<string, string>;
  compactAvailabilityTagClassName?: string;
  compactAvailabilityTagClassNameByKey?: Record<string, string>;
  compactTitleClassName?: string;
  titleClassName?: string;
  tooltipClassName?: string;
  priceRegularFontSize?: 14 | 16;
  priceRegularClassName?: string;
  priceAsHeading?: boolean;
  priceMedium?: boolean;
}

export type ProductTileLookProps = Pick<
  ProductTileProps,
  | 'imageApi'
  | 'imageWrapperClassName'
  | 'imageAspectClassName'
  | 'imageObjectFit'
  | 'availabilityTagsClassName'
  | 'availabilityTagClassName'
  | 'availabilityTagClassNameByKey'
  | 'compactAvailabilityTagClassName'
  | 'compactAvailabilityTagClassNameByKey'
  | 'compactTitleClassName'
  | 'titleClassName'
  | 'tooltipClassName'
  | 'priceRegularFontSize'
  | 'priceRegularClassName'
  | 'priceAsHeading'
  | 'priceMedium'
>;

const ProductTile: FC<ProductTileProps> = ({
  product,
  onClick,
  isSearchResult = false,
  disableVariants = false,
  sustainability,
  style,
  dataTestId,
  index = 0,
  showSecondImageOnHover = false,
  imageApi = 'fill',
  imageWrapperClassName = '',
  imageAspectClassName = 'aspect-[5/7]',
  imageObjectFit = 'cover',
  availabilityTagsClassName = 'absolute right-10 top-10 z-10',
  availabilityTagClassName = 'font-primaryThin',
  availabilityTagClassNameByKey = { 'coming-soon': '!text-accent !border-accent font-primaryBold' },
  titleClassName = 'text-md w-full min-w-0 overflow-hidden break-words line-clamp-2 pt-15 font-semibold leading-[1.2]',
  tooltipClassName,
  priceRegularFontSize,
  priceRegularClassName,
  priceAsHeading,
  priceMedium,
}) => {
  const [isDesktopSize] = useMediaQuery(desktop);

  const { ref } = useTrack({ product });
  const { getImpactByProduct } = useImpacts();
  const { showColorSwatches } = useProductTileFeatures();
  const [iconsData, setIconData] = useState<IconText[]>([{ icon: <></>, text: '', units: '' }]);

  const variantWithDiscount = useVariantWithDiscount(product.variants) as Variant;

  const discountedPrice = useMemo(() => variantWithDiscount?.discountedPrice, [variantWithDiscount]);

  const discountPercentage = useMemo(
    () =>
      variantWithDiscount
        ? (((variantWithDiscount.price?.centAmount as number) - (discountedPrice?.centAmount as number)) /
            (variantWithDiscount.price?.centAmount as number)) *
          100
        : 0,
    [discountedPrice, variantWithDiscount],
  );

  const [selectedVariant, setSelectedVariant] = useState(() => variantWithDiscount ?? product?.variants[0]);

  const colorOptions = useMemo(() => getProductColorOptions(product.variants), [product.variants]);

  const [selectedColorKey, setSelectedColorKey] = useState<string | undefined>(
    () => (selectedVariant?.attributes?.color?.key as string | undefined) ?? colorOptions[0]?.key,
  );

  const [displayedImage, setDisplayedImage] = useState<string | undefined>(() => selectedVariant?.images?.[0]);

  const handleColorSelect = useCallback((color: ProductColorOption) => {
    setSelectedColorKey(color.key);
    // Colors without an image keep the currently displayed one rather than
    // falling back to a placeholder.
    if (color.imageUrl) setDisplayedImage(color.imageUrl);
  }, []);

  const productUrl = useMemo(() => {
    if (!product._url) return '#';

    if (isSearchResult) return `${product._url}?sr=1`;
    return product._url;
  }, [product._url, isSearchResult]);

  const productAvailabilityTags = product?.attributes?.['product-availability-tags'] || [];

  const variantImages = selectedVariant.images ?? [];
  const secondaryImage = variantImages.length > 1 ? variantImages[variantImages.length - 1] : undefined;
  const shouldShowSecondImageOnHover = showSecondImageOnHover && !!secondaryImage;

  useEffect(() => {
    setIconData(getImpactByProduct(product.variants[0], sustainability));
  }, [product.variants, sustainability]);

  const shouldShowSustentability = () => {
    return sustainability?.environmentalItem?.icons?.length && sustainability?.environmentalItem?.shouldShowIcons;
  };

  const objectFitClass = imageObjectFit === 'contain' ? 'object-contain' : 'object-cover';

  return (
    <div
      onClick={onClick}
      ref={ref}
      data-component-group="product-card"
      data-testid={dataTestId ?? `product-tile-${index}`}
      className="relative border p-20 flex flex-col h-full"
      style={style}
    >
      <div className="relative">
        <NextLink href={productUrl} data-testid={`product-image-${index}`}>
          <div className={`group relative w-full ${imageAspectClassName} overflow-hidden ${imageWrapperClassName}`}>
            {imageApi === 'layout' ? (
              <Image
                src={displayedImage}
                layout="fill"
                objectFit={imageObjectFit}
                objectPosition="center"
                priority={true}
                suffix="large"
                alt={product.name || ''}
                className={`rounded-sm ${shouldShowSecondImageOnHover ? 'group-hover:hidden' : ''}`}
              />
            ) : (
              <Image
                src={displayedImage}
                fill
                priority={true}
                suffix="large"
                alt={product.name || ''}
                className={`${objectFitClass} object-center rounded-sm ${shouldShowSecondImageOnHover ? 'group-hover:hidden' : ''}`}
              />
            )}
            {shouldShowSecondImageOnHover &&
              (imageApi === 'layout' ? (
                <Image
                  src={secondaryImage}
                  layout="fill"
                  objectFit={imageObjectFit}
                  objectPosition="center"
                  suffix="large"
                  alt={product.name || ''}
                  className="absolute inset-0 hidden rounded-sm group-hover:block"
                />
              ) : (
                <Image
                  src={secondaryImage}
                  fill
                  suffix="large"
                  alt={product.name || ''}
                  className={`absolute inset-0 hidden ${objectFitClass} object-center rounded-sm group-hover:block`}
                />
              ))}
          </div>
        </NextLink>

        {productAvailabilityTags.length > 0 && (
          <div className={availabilityTagsClassName} data-testid={`availability-tags-${index}`}>
            <ProductAvailabilityTags
              tags={productAvailabilityTags}
              tagClassName={availabilityTagClassName}
              tagClassNameByKey={availabilityTagClassNameByKey}
              layout="stacked"
            />
          </div>
        )}
      </div>

      <div className="flex flex-1 flex-col">
        <NextLink href={productUrl} className="flex flex-col" data-testid={`view-product-${index}`}>
          <div data-testid={dataTestId ? `${dataTestId}-title` : `product-name-${index}`} className={titleClassName}>
            {product?.name}
          </div>

          <div data-testid={dataTestId ? `${dataTestId}-subtitle` : undefined} className="pt-15 leading-[1]">
            {selectedVariant?.price?.centAmount && selectedVariant?.price?.centAmount > 0 ? (
              <Prices
                price={variantWithDiscount?.price ?? selectedVariant?.price}
                discountedPrice={variantWithDiscount?.discountedPrice}
                regularFontSize={priceRegularFontSize}
                regularClassName={priceRegularClassName}
                asHeading={priceAsHeading}
                medium={priceMedium}
                index={index}
              />
            ) : (
              <div className="h-21"></div>
            )}
          </div>
        </NextLink>

        {showColorSwatches && !disableVariants && (
          <ProductColorSwatches
            colors={colorOptions}
            selectedColorKey={selectedColorKey}
            onSelect={handleColorSelect}
            moreHref={productUrl}
            linkComponent={NextLink}
            className="pt-12"
          />
        )}

        {shouldShowSustentability() && (
          <SustainabilityBadge
            iconsWithText={iconsData}
            environmentalTextColor={sustainability?.environmentalItem?.textColor}
            className="mt-auto pt-12"
            tooltipClassName={tooltipClassName}
            index={index}
          />
        )}
      </div>
    </div>
  );
};

export default ProductTile;
